/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {ReservationsTableRequest} from "store/bookingLogin/BookingLoginAction";
import {Reservation} from "types/entities/Reservation";
import SellerAction from "store/seller/SellerAction";
import update from "immutability-helper";
import {getStartAndEndOfWeek} from "helpers/helpers";
import {ReservationNote} from "store/booking/BookingAction";
import {PackagesCart} from "types/entities/PackagesCart";
import {ReservationPlan} from "types/entities/ReservationPlan";
import {
    SellerExtraInvoiceNoteToReservation,
    SellerExtraOfferNoteToReservation,
} from "types/entities/SellerReservations";

const {startOfWeek, endOfWeek} = getStartAndEndOfWeek(new Date());

export interface SellerReducerSetTableState {
    payload: ReservationsTableRequest;
    type: typeof SellerAction.SET_TABLE_STATE;
}

export interface SellerReducerSetReservations {
    payload: Reservation[];
    type: typeof SellerAction.SET_RESERVATIONS;
}

export interface SellerReducerSetReservation {
    payload: Reservation;
    type: typeof SellerAction.SET_RESERVATION;
}

export interface SellerReducerDeleteReservation {
    payload: Reservation;
    type: typeof SellerAction.DELETE_RESERVATION;
}

export interface SellerReducerClearReservations {
    payload: [];
    type: typeof SellerAction.CLEAR_RESERVATIONS;
}

export interface SellerReducerClearTableState {
    payload: null;
    type: typeof SellerAction.CLEAR_TABLE_STATE;
}

export interface SellerReducerPushReservationNote {
    payload: ReservationNote;
    type: typeof SellerAction.PUSH_RESERVATION_NOTE;
}

export interface SellerReducerSetPackageCartToReservation {
    payload: PackagesCart;
    type: typeof SellerAction.SET_PACKAGE_CART_TO_RESERVATION;
}

export interface SellerReducerSetReservationPlans {
    payload: {reservation_id: number, plans: ReservationPlan[]};
    type: typeof SellerAction.SET_RESERVATION_PLANS;
}

export interface SellerReducerSetExtraInvoiceNoteToReservation {
    payload: SellerExtraInvoiceNoteToReservation;
    type: typeof SellerAction.SET_EXTRA_INVOICE_NOTE_TO_RESERVATION;
}

export interface SellerReducerSetExtraOfferNoteToReservation {
    payload: SellerExtraOfferNoteToReservation;
    type: typeof SellerAction.SET_EXTRA_OFFER_NOTE_TO_RESERVATION;
}

type SellerReducerAction =
    SellerReducerClearTableState
    | SellerReducerSetReservations
    | SellerReducerSetReservation
    | SellerReducerSetTableState
    | SellerReducerClearReservations
    | SellerReducerDeleteReservation
    | SellerReducerPushReservationNote
    | SellerReducerSetPackageCartToReservation
    | SellerReducerSetReservationPlans
    | SellerReducerSetExtraOfferNoteToReservation
    | SellerReducerSetExtraInvoiceNoteToReservation;

export interface SellerReducerState {
    reservations: Reservation[];
    tableState: ReservationsTableRequest;
}


class SellerReducer {

    initialState: SellerReducerState = {
        reservations: [],
        tableState: {
            filter: {},
            page: 0,
            per_page: 20,
            search: [{id: "week", value: [startOfWeek, endOfWeek]}],
            sorting: [{id: "created_at", desc: true}],
        },
    };

    reducer = (state: SellerReducerState = this.initialState, action: SellerReducerAction): SellerReducerState => {
        function updateReservation(state: SellerReducerState, reservationIndex: number, changeset: any) {
            return update(state, {
                reservations: {
                    [reservationIndex]: changeset,
                },
            } as any);
        }

        switch (action.type) {
            case SellerAction.SET_RESERVATIONS:
                return {
                    ...state,
                    reservations: action.payload,
                };

            case SellerAction.SET_RESERVATION:
                const foundReservationIndex = state.reservations.findIndex(reservation => reservation.id === action.payload.id);

                if (foundReservationIndex === -1) {
                    return update(state, {
                        reservations: {
                            $push: [action.payload],
                        },
                    });
                }

                return <SellerReducerState>update(state, {
                    reservations: {
                        [foundReservationIndex]: {
                            $set: action.payload,
                        },
                    },
                });

            case SellerAction.SET_TABLE_STATE:
                return {
                    ...state,
                    tableState: action.payload,
                };

            case SellerAction.CLEAR_RESERVATIONS:
                return {
                    ...state,
                    reservations: action.payload,
                };

            case SellerAction.CLEAR_TABLE_STATE:
                return {
                    ...state,
                    tableState: this.initialState.tableState,
                };

            case SellerAction.DELETE_RESERVATION:
                const foundRemoveReservationIndex = state.reservations.findIndex(reservation => reservation.id === action.payload.id);

                if (foundRemoveReservationIndex === -1) {
                    return state;
                }

                return update(state, {
                    reservations: {
                        $splice: [[foundRemoveReservationIndex, 1]],
                    },
                });

            case SellerAction.PUSH_RESERVATION_NOTE:
                const reservationForNoteIndex = state.reservations.findIndex(reservation => reservation.id === action.payload.reservation_id);

                if (reservationForNoteIndex === -1) {
                    return state;
                }

                const noteIndex = state.reservations[reservationForNoteIndex].reservationNotes.findIndex(note => note.id === action.payload.id);

                if (noteIndex === -1) {
                    return updateReservation(state, reservationForNoteIndex, {
                        reservationNotes: {$push: [action.payload]},
                    } as any);
                }

                return updateReservation(state, reservationForNoteIndex, {
                    reservationNotes: {[noteIndex]: {$set: action.payload}},
                } as any);

            case SellerAction.SET_PACKAGE_CART_TO_RESERVATION:
                const reservationForPackageCartIndex = state.reservations.findIndex(reservation => reservation.id === action.payload.reservation_id);
                if (reservationForPackageCartIndex === -1) {
                    return state;
                }

                return updateReservation(state, reservationForPackageCartIndex, {
                    packagesCart: {
                        $set: action.payload,
                    },
                } as any);

            case SellerAction.SET_RESERVATION_PLANS:
                const reservationForPlansIndex = state.reservations.findIndex(reservation => reservation.id === action.payload.reservation_id);

                if (reservationForPlansIndex === -1) {
                    return state;
                }

                return updateReservation(state, reservationForPlansIndex, {
                    extra: {
                        reservation_plan: {$set: action.payload.plans},
                    }
                } as any);

            case SellerAction.SET_EXTRA_INVOICE_NOTE_TO_RESERVATION:
                const reservationForExtraInvoiceNoteIndex = state.reservations.findIndex(reservation => reservation.id === action.payload.reservation_id);

                if (reservationForExtraInvoiceNoteIndex === -1) {
                    return state;
                }

                return updateReservation(state, reservationForExtraInvoiceNoteIndex, {
                    extra: {
                        invoice_note: {$set: action.payload.invoice_note},
                    }
                } as any);

            case SellerAction.SET_EXTRA_OFFER_NOTE_TO_RESERVATION:
                const reservationForExtraOfferNoteIndex = state.reservations.findIndex(reservation => reservation.id === action.payload.reservation_id);

                if (reservationForExtraOfferNoteIndex === -1) {
                    return state;
                }

                return updateReservation(state, reservationForExtraOfferNoteIndex, {
                    extra: {
                        invoice_note: {$set: action.payload.offer_note},
                    }
                } as any);

            default:
                return state;
        }
    };
}

export default SellerReducer;
